import { useState } from 'react';
import { Group, Loader, Select, Stack, Switch, Tabs, Text, TextInput, Title } from '@mantine/core';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCompanies } from 'src/secret-menu/hooks/useCompanies';
import { FeatureFlagStatus, useAdminFeatureFlags } from './useAdminFeatureFlags';
import { useFlagMatrix } from './useFlagMatrix';
import FlagMatrixGrid, { TenantColumn } from './FlagMatrixGrid';
import FlagCentricList from './FlagCentricList';
import TenantCentricList from './TenantCentricList';
import FlagDetailDrawer, { FlagMatrixSelection } from './FlagDetailDrawer';
import { StateLegend, TenantFailureAlert } from './FlagMatrixShared';
import { CellState, StateCounts, TenantEvaluation, emptyStateCounts } from './flagMatrixTypes';

const STATUS_OPTIONS = [
  { value: 'fully_released', label: 'Fully released' },
  { value: 'partially_released', label: 'Partially released' },
  { value: 'disabled', label: 'Disabled' },
];

export default function FlagMatrixPage() {
  const { flags, isLoading: flagsLoading, isError: flagsError } = useAdminFeatureFlags();
  const { companies, isLoading: companiesLoading } = useCompanies();
  const [selection, setSelection] = useState<FlagMatrixSelection | null>(null);
  const [flagSearch, setFlagSearch] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FeatureFlagStatus | null>(null);
  const [internalOnly, setInternalOnly] = useState(false);

  const sortedCompanies = (companies ?? [])
    .slice()
    .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  const tenantNames = sortedCompanies.map(company => company.tenantName);

  const { matrix, isLoading: matrixLoading, isError: matrixError } = useFlagMatrix(tenantNames);

  const evaluationByTenant = new Map<string, TenantEvaluation>();
  (matrix ?? []).forEach(entry => evaluationByTenant.set(entry.tenantName, entry));

  const tenantColumns: TenantColumn[] = sortedCompanies.map(company => ({
    tenantName: company.tenantName,
    label: company.tenantName,
    internal: company.isInternal,
    error: evaluationByTenant.get(company.tenantName)?.error ?? null,
  }));

  const stateFor = (flagKey: string, tenantName: string): CellState => {
    const evaluation = evaluationByTenant.get(tenantName);
    if (!evaluation) return 'unknown';
    if (evaluation.error !== null) return 'failed';
    return evaluation.states[flagKey] ?? 'unknown';
  };

  const sortedFlags = (flags ?? []).slice().sort((a, b) => a.key.localeCompare(b.key));

  const flagCountsFor = (flagKey: string): StateCounts => {
    const counts = emptyStateCounts();
    tenantColumns.forEach(tenant => {
      counts[stateFor(flagKey, tenant.tenantName)] += 1;
    });
    return counts;
  };
  const tenantCountsFor = (tenantName: string): StateCounts => {
    const counts = emptyStateCounts();
    sortedFlags.forEach(flag => {
      counts[stateFor(flag.key, tenantName)] += 1;
    });
    return counts;
  };

  if (flagsLoading || companiesLoading) {
    return <PageLoader />;
  }
  if (flagsError) {
    return <Text c="red">Failed to load feature flags.</Text>;
  }

  const matrixFlags = sortedFlags.filter(
    flag =>
      (!statusFilter || flag.status === statusFilter) &&
      (!flagSearch || flag.key.toLowerCase().includes(flagSearch.trim().toLowerCase())),
  );
  const matrixTenants = tenantColumns.filter(
    tenant =>
      (!internalOnly || tenant.internal) &&
      (!tenantSearch ||
        tenant.tenantName.toLowerCase().includes(tenantSearch.trim().toLowerCase())),
  );

  const notReady = matrixLoading || !matrix;
  const loadingBlock = matrixError ? (
    <Text c="red">Failed to evaluate flags across tenants.</Text>
  ) : (
    <Group gap="sm" py="lg">
      <Loader size="sm" />
      <Text size="sm" c="dimmed">
        Evaluating {sortedFlags.length} flags across {tenantColumns.length} tenants…
      </Text>
    </Group>
  );

  return (
    <Stack gap="md">
      <Group align="baseline" gap="sm">
        <Title order={3}>Flag matrix</Title>
        <Text size="sm" c="dimmed" ff="monospace">
          production · {sortedFlags.length} flags · {tenantColumns.length} tenants
        </Text>
      </Group>

      <StateLegend showFailed={tenantColumns.some(tenant => tenant.error !== null)} />

      <TenantFailureAlert tenants={tenantColumns} />

      <Tabs defaultValue="matrix">
        <Tabs.List>
          <Tabs.Tab value="matrix">Matrix</Tabs.Tab>
          <Tabs.Tab value="flag">By flag</Tabs.Tab>
          <Tabs.Tab value="tenant">By tenant</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="matrix" pt="md">
          <Stack gap="sm">
            <Group>
              <TextInput
                placeholder="Search flags"
                value={flagSearch}
                onChange={event => setFlagSearch(event.currentTarget.value)}
              />
              <TextInput
                placeholder="Filter tenants"
                value={tenantSearch}
                onChange={event => setTenantSearch(event.currentTarget.value)}
              />
              <Select
                placeholder="All statuses"
                clearable
                data={STATUS_OPTIONS}
                value={statusFilter}
                onChange={value => setStatusFilter(value as FeatureFlagStatus | null)}
              />
              <Switch
                label="Internal only"
                checked={internalOnly}
                onChange={event => setInternalOnly(event.currentTarget.checked)}
              />
            </Group>
            {notReady ? (
              loadingBlock
            ) : (
              <FlagMatrixGrid
                flags={matrixFlags}
                tenants={matrixTenants}
                stateFor={stateFor}
                onSelectCell={(flagKey, tenantName) =>
                  setSelection({ kind: 'cell', flagKey, tenantName })
                }
              />
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="flag" pt="md">
          {notReady ? (
            loadingBlock
          ) : (
            <FlagCentricList
              flags={sortedFlags}
              countsFor={flagCountsFor}
              tenantTotal={tenantColumns.length}
              onSelectFlag={flagKey => setSelection({ kind: 'flag', flagKey })}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="tenant" pt="md">
          {notReady ? (
            loadingBlock
          ) : (
            <TenantCentricList
              tenants={tenantColumns}
              countsFor={tenantCountsFor}
              flagTotal={sortedFlags.length}
              onSelectTenant={tenantName => setSelection({ kind: 'tenant', tenantName })}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      <FlagDetailDrawer
        selection={selection}
        flags={sortedFlags}
        tenants={tenantColumns}
        stateFor={stateFor}
        onSelectCell={(flagKey, tenantName) => setSelection({ kind: 'cell', flagKey, tenantName })}
        onClose={() => setSelection(null)}
      />
    </Stack>
  );
}
